import React from "react";
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  CreditCard as CreditCardIcon,
  Receipt as ReceiptIcon,
  LocationCity as LocationCityIcon,
  AccountBalance as AccountBalanceIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Category as CategoryIcon,
  Inventory as InventoryIcon,
  Warehouse as WarehouseIcon,
  ShoppingCart as ShoppingCartIcon,
  LocalMall as LocalMallIcon,
  TableBar as TableBarIcon,
  RestaurantMenu as RestaurantMenuIcon,
  Assessment as AssessmentIcon,
  Payment,
  Nfc as NfcIcon,
  Settings as SettingsIcon,
  AdminPanelSettings as RolesIcon,
  Business as BusinessIcon,
  Storefront as StorefrontIcon,
  PointOfSale as PointOfSaleIcon,
  Contactless as ContactlessIcon,
  BarChart as BarChartIcon,
  Build as BuildIcon,
} from "@mui/icons-material";

export type AdminNavLeaf = {
  kind: "leaf";
  id: string;
  text: string;
  icon: React.ReactNode;
  path: string;
  module: string;
  requireRecharge?: boolean;
};

export type AdminNavGroup = {
  kind: "group";
  id: string;
  text: string;
  icon: React.ReactNode;
  children: AdminNavLeaf[];
};

export type AdminNavEntry = AdminNavLeaf | AdminNavGroup;

export type AdminNavSection = {
  id: string;
  label: string;
  entries: AdminNavEntry[];
};

const ADMIN_REPORT_PATHS = new Set([
  "/reports",
  "/reports/sales",
  "/reports/gst-books",
  "/reports/chart-of-accounts",
  "/reports/journals",
  "/reports/inventory-alerts",
  "/vouchers",
]);

export function isAdminReportPath(path: string): boolean {
  return ADMIN_REPORT_PATHS.has(path);
}

export type BuildAdminNavOptions = {
  isSuperAdmin: boolean;
  hasAnyRole: (...roles: string[]) => boolean;
};

/** Logical sidebar order with grouped submenus. */
export function buildAdminNavSections({
  isSuperAdmin,
  hasAnyRole,
}: BuildAdminNavOptions): AdminNavSection[] {
  const productListLabel = hasAnyRole(
    "OPERATOR",
    "SALES_POINT_ADMIN",
    "MERCHANT",
    "GROCERY_STORE"
  )
    ? "Sales Point Products"
    : "Product List";

  const tablesLabel = hasAnyRole("OPERATOR", "SALES_POINT_ADMIN", "MERCHANT")
    ? "Sales Point Tables"
    : "Table Setup";

  const organizationChildren: AdminNavLeaf[] = [
    {
      kind: "leaf",
      id: "branches",
      text: "Branches",
      icon: <LocationCityIcon />,
      path: "/branches",
      module: "branches",
    },
    {
      kind: "leaf",
      id: "sales-points",
      text: "Sales Points",
      icon: <AccountBalanceIcon />,
      path: "/sales-points",
      module: "sales_points",
    },
    {
      kind: "leaf",
      id: "users",
      text: "Users",
      icon: <PeopleIcon />,
      path: "/users",
      module: "users",
    },
  ];

  if (isSuperAdmin) {
    organizationChildren.push({
      kind: "leaf",
      id: "roles",
      text: "Roles",
      icon: <RolesIcon />,
      path: "/roles",
      module: "permissions",
    });
  }

  const catalogChildren: AdminNavLeaf[] = [
    {
      kind: "leaf",
      id: "categories",
      text: "Categories",
      icon: <CategoryIcon />,
      path: "/categories",
      module: "categories",
    },
    {
      kind: "leaf",
      id: "product-list",
      text: productListLabel,
      icon: <InventoryIcon />,
      path: "/product-list",
      module: "product_sales_points",
    },
    {
      kind: "leaf",
      id: "recipes",
      text: "Recipes / BOM",
      icon: <RestaurantMenuIcon />,
      path: "/recipes",
      module: "recipes",
    },
    {
      kind: "leaf",
      id: "inventory",
      text: "Inventory",
      icon: <WarehouseIcon />,
      path: "/inventory",
      module: "inventory",
    },
  ];

  const procurementChildren: AdminNavLeaf[] = [
    {
      kind: "leaf",
      id: "purchases",
      text: "Purchases",
      icon: <LocalMallIcon />,
      path: "/purchases",
      module: "purchases",
    },
    {
      kind: "leaf",
      id: "suppliers",
      text: "Suppliers",
      icon: <PeopleIcon />,
      path: "/suppliers",
      module: "purchases",
    },
  ];

  const salesChildren: AdminNavLeaf[] = [
    {
      kind: "leaf",
      id: "sales",
      text: "Sales / POS",
      icon: <ShoppingCartIcon />,
      path: "/sales",
      module: "sales",
    },
    {
      kind: "leaf",
      id: "tables",
      text: tablesLabel,
      icon: <TableBarIcon />,
      path: "/tables",
      module: "tables",
    },
    {
      kind: "leaf",
      id: "customers",
      text: "Customers",
      icon: <PeopleIcon />,
      path: "/customers",
      module: "sales",
    },
  ];

  const nfcChildren: AdminNavLeaf[] = [
    {
      kind: "leaf",
      id: "cardholders",
      text: "Card Holders",
      icon: <CreditCardIcon />,
      path: "/cardholders",
      module: "cardholders",
    },
    {
      kind: "leaf",
      id: "nfc-cards",
      text: "NFC Cards",
      icon: <NfcIcon />,
      path: "/nfc-cards",
      module: "nfc_cards",
    },
    {
      kind: "leaf",
      id: "quick-recharge",
      text: "Quick Recharge",
      icon: <Payment />,
      path: "/quick-recharge",
      module: "recharges",
      requireRecharge: true,
    },
    {
      kind: "leaf",
      id: "check-balance",
      text: "Check Balance",
      icon: <AccountBalanceWalletIcon />,
      path: "/check-balance",
      module: "nfc_cards",
    },
    {
      kind: "leaf",
      id: "recharges",
      text: "Recharges",
      icon: <AccountBalanceIcon />,
      path: "/recharges",
      module: "recharges",
    },
    {
      kind: "leaf",
      id: "transactions",
      text: "Transactions",
      icon: <ReceiptIcon />,
      path: "/transactions",
      module: "transactions",
    },
  ];

  const fieldStaffReportChildren: AdminNavLeaf[] = [
    {
      kind: "leaf",
      id: "my-reports",
      text: "My Reports",
      icon: <AssessmentIcon />,
      path: "/my-reports",
      module: "reports",
    },
  ];

  const adminReportChildren: AdminNavLeaf[] = [
    {
      kind: "leaf",
      id: "reports-hub",
      text: "Reports hub",
      icon: <AssessmentIcon />,
      path: "/reports",
      module: "reports",
    },
    {
      kind: "leaf",
      id: "reports-sales",
      text: "Sales report",
      icon: <AssessmentIcon />,
      path: "/reports/sales",
      module: "reports",
    },
    {
      kind: "leaf",
      id: "reports-gst",
      text: "GST & books",
      icon: <AssessmentIcon />,
      path: "/reports/gst-books",
      module: "reports",
    },
    {
      kind: "leaf",
      id: "reports-coa",
      text: "Chart of accounts",
      icon: <AssessmentIcon />,
      path: "/reports/chart-of-accounts",
      module: "reports",
    },
    {
      kind: "leaf",
      id: "reports-journals",
      text: "Journals",
      icon: <AssessmentIcon />,
      path: "/reports/journals",
      module: "reports",
    },
    {
      kind: "leaf",
      id: "reports-inventory-alerts",
      text: "Inventory alerts",
      icon: <InventoryIcon />,
      path: "/reports/inventory-alerts",
      module: "reports",
    },
    {
      kind: "leaf",
      id: "vouchers",
      text: "Vouchers",
      icon: <ReceiptIcon />,
      path: "/vouchers",
      module: "reports",
    },
  ];

  const systemChildren: AdminNavLeaf[] = [
    {
      kind: "leaf",
      id: "settings",
      text: "Settings",
      icon: <SettingsIcon />,
      path: "/settings",
      module: "settings",
    },
  ];

  if (isSuperAdmin) {
    systemChildren.push({
      kind: "leaf",
      id: "audit-logs",
      text: "Audit logs",
      icon: <AssessmentIcon />,
      path: "/audit-logs",
      module: "audit_logs",
    });
  }

  return [
    {
      id: "overview",
      label: "Overview",
      entries: [
        {
          kind: "leaf",
          id: "dashboard",
          text: "Dashboard",
          icon: <DashboardIcon />,
          path: "/dashboard",
          module: "dashboard",
        },
      ],
    },
    {
      id: "organization",
      label: "Organization",
      entries: [
        {
          kind: "group",
          id: "organization-group",
          text: "Organization",
          icon: <BusinessIcon />,
          children: organizationChildren,
        },
      ],
    },
    {
      id: "catalog",
      label: "Catalog & stock",
      entries: [
        {
          kind: "group",
          id: "catalog-group",
          text: "Catalog",
          icon: <StorefrontIcon />,
          children: catalogChildren,
        },
        {
          kind: "group",
          id: "procurement-group",
          text: "Procurement",
          icon: <LocalMallIcon />,
          children: procurementChildren,
        },
      ],
    },
    {
      id: "sales",
      label: "Sales",
      entries: [
        {
          kind: "group",
          id: "sales-group",
          text: "Sales & POS",
          icon: <PointOfSaleIcon />,
          children: salesChildren,
        },
      ],
    },
    {
      id: "nfc",
      label: "NFC & wallet",
      entries: [
        {
          kind: "group",
          id: "nfc-group",
          text: "NFC & cards",
          icon: <ContactlessIcon />,
          children: nfcChildren,
        },
      ],
    },
    {
      id: "reports",
      label: "Reports",
      entries: [
        {
          kind: "group",
          id: "reports-group",
          text: "Reports & finance",
          icon: <BarChartIcon />,
          children: [...fieldStaffReportChildren, ...adminReportChildren],
        },
      ],
    },
    {
      id: "system",
      label: "System",
      entries: [
        {
          kind: "group",
          id: "system-group",
          text: "System",
          icon: <BuildIcon />,
          children: systemChildren,
        },
      ],
    },
  ];
}

export type AdminNavFilterContext = {
  isSuperAdmin: boolean;
  isFieldStaff: boolean;
  showQuickRecharge: boolean;
  canRead: (module: string) => boolean;
};

export function isNavLeafVisible(
  leaf: AdminNavLeaf,
  ctx: AdminNavFilterContext
): boolean {
  if (leaf.path === "/roles" || leaf.path === "/audit-logs") {
    return ctx.isSuperAdmin;
  }
  if (!ctx.canRead(leaf.module)) return false;
  if (leaf.requireRecharge && !ctx.showQuickRecharge) return false;
  if (leaf.path === "/products") return false;
  if (leaf.path === "/my-reports") return ctx.isFieldStaff;
  if (isAdminReportPath(leaf.path)) return !ctx.isFieldStaff;
  return true;
}

function filterGroup(
  group: AdminNavGroup,
  ctx: AdminNavFilterContext
): AdminNavGroup | null {
  const children = group.children.filter((c) => isNavLeafVisible(c, ctx));
  if (!children.length) return null;
  return { ...group, children };
}

/** Filter sections/entries by RBAC; drop empty groups and sections. */
export function filterAdminNavSections(
  sections: AdminNavSection[],
  ctx: AdminNavFilterContext
): AdminNavSection[] {
  return sections
    .map((section) => {
      const entries = section.entries
        .map((entry) => {
          if (entry.kind === "leaf") {
            return isNavLeafVisible(entry, ctx) ? entry : null;
          }
          return filterGroup(entry, ctx);
        })
        .filter(Boolean) as AdminNavEntry[];
      if (!entries.length) return null;
      return { ...section, entries };
    })
    .filter(Boolean) as AdminNavSection[];
}

/** Flat leaf list for search + active-route matching. */
export function flattenAdminNavLeaves(
  sections: AdminNavSection[]
): AdminNavLeaf[] {
  const leaves: AdminNavLeaf[] = [];
  for (const section of sections) {
    for (const entry of section.entries) {
      if (entry.kind === "leaf") leaves.push(entry);
      else leaves.push(...entry.children);
    }
  }
  return leaves;
}

/** Groups whose child matches pathname should stay expanded. */
export function groupsContainingPath(
  sections: AdminNavSection[],
  pathname: string
): string[] {
  const ids: string[] = [];
  for (const section of sections) {
    for (const entry of section.entries) {
      if (entry.kind !== "group") continue;
      const hit = entry.children.some(
        (c) => pathname === c.path || pathname.startsWith(`${c.path}/`)
      );
      if (hit) ids.push(entry.id);
    }
  }
  return ids;
}
